(() => {
  class RrProductGallery extends HTMLElement {
    connectedCallback() {
      if (this.dataset.rrReady === 'true') return;

      this.list = this.querySelector('.rr-product-gallery__list');
      this.prevButton = this.querySelector('.rr-product-gallery__nav--prev');
      this.nextButton = this.querySelector('.rr-product-gallery__nav--next');
      this.dotsRoot = this.querySelector('.rr-product-gallery__dots');

      if (!this.list) return;

      this.dataset.rrReady = 'true';
      this.index = 0;

      this.onScroll = () => this.syncFromScroll();
      this.list.addEventListener('scroll', this.onScroll, { passive: true });

      if (this.prevButton) {
        this.prevButton.addEventListener('click', () => this.goTo(this.index - 1));
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', () => this.goTo(this.index + 1));
      }

      if (this.dotsRoot) {
        this.dotsRoot.addEventListener('click', (event) => {
          const dot = event.target.closest('.rr-product-gallery__dot');

          if (!dot) return;

          this.goTo(Number(dot.dataset.index));
        });
      }

      this.observer = new MutationObserver(() => {
        this.rebuildDots();
        this.bindHoverZoom();
        this.syncFromScroll();
      });
      this.observer.observe(this.list, { childList: true });

      this.bindHoverZoom();
      this.syncFromScroll();
    }

    disconnectedCallback() {
      this.list?.removeEventListener('scroll', this.onScroll);
      this.observer?.disconnect();
    }

    slides() {
      return Array.from(this.list.querySelectorAll('.rr-product-gallery__slide[data-media-id]'));
    }

    bindHoverZoom() {
      if (window.innerWidth < 1025) return;

      for (const slide of this.slides()) {
        this.initSlideHoverZoom(slide);
      }
    }

    initSlideHoverZoom(slide) {
      if (slide.dataset.rrZoom === 'true') return;
      if (slide.getAttribute('data-media-type') !== 'image') return;

      const image = slide.querySelector(':scope > img');

      if (!image) return;

      slide.dataset.rrZoom = 'true';

      const isImageSlide = () => slide.getAttribute('data-media-type') === 'image';

      const onEnter = () => {
        if (!isImageSlide()) return;

        image.style.opacity = '0';
        slide.style.backgroundImage = `url("${image.currentSrc}")`;
        slide.style.backgroundRepeat = 'no-repeat';
        slide.style.backgroundSize = '200%';
      };

      const onMove = (event) => {
        if (!isImageSlide()) return;

        const target = event.currentTarget;
        const x = event.offsetX || 0;
        const y = event.offsetY || 0;
        const xPercent = (x / target.offsetWidth) * 100;
        const yPercent = (y / target.offsetHeight) * 100;

        target.style.backgroundPosition = `${xPercent}% ${yPercent}%`;
      };

      const onLeave = () => {
        if (!isImageSlide()) return;

        image.style.opacity = '1';
        slide.style.backgroundImage = '';
      };

      slide.addEventListener('mouseenter', onEnter);
      slide.addEventListener('mousemove', onMove);
      slide.addEventListener('mouseleave', onLeave);
    }

    goTo(nextIndex) {
      const slides = this.slides();

      if (slides.length === 0) return;

      const index = Math.min(Math.max(nextIndex, 0), slides.length - 1);
      const width = this.list.clientWidth;

      this.list.scrollTo({ left: index * width, behavior: 'smooth' });
      this.setIndex(index);
    }

    setActiveMedia(mediaId, prepend) {
      const slides = this.slides();
      const index = slides.findIndex((slide) => slide.dataset.mediaId === mediaId);

      if (index < 0) return;

      if (prepend && index > 0) {
        this.list.prepend(slides[index]);
        this.rebuildDots();
        this.goTo(0);
        return;
      }

      this.goTo(index);
    }

    syncFromScroll() {
      const width = this.list.clientWidth;

      if (width === 0) return;

      const index = Math.round(this.list.scrollLeft / width);
      this.setIndex(index);
    }

    setIndex(index) {
      const slides = this.slides();
      const safeIndex = Math.min(Math.max(index, 0), Math.max(slides.length - 1, 0));
      this.index = safeIndex;

      for (const [slideIndex, slide] of slides.entries()) {
        slide.classList.toggle('is-active', slideIndex === safeIndex);
      }

      if (this.prevButton) {
        this.prevButton.hidden = safeIndex <= 0 || slides.length < 2;
      }

      if (this.nextButton) {
        this.nextButton.hidden = safeIndex >= slides.length - 1 || slides.length < 2;
      }

      if (!this.dotsRoot) return;

      const dots = this.dotsRoot.querySelectorAll('.rr-product-gallery__dot');

      for (const [dotIndex, dot] of dots.entries()) {
        if (dotIndex === safeIndex) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      }
    }

    rebuildDots() {
      if (!this.dotsRoot) return;

      const slides = this.slides();
      const currentCount = this.dotsRoot.querySelectorAll('.rr-product-gallery__dot').length;

      if (currentCount === slides.length) return;

      this.dotsRoot.replaceChildren();

      for (const [index] of slides.entries()) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'rr-product-gallery__dot';
        dot.dataset.index = String(index);
        this.dotsRoot.append(dot);
      }
    }
  }

  if (!customElements.get('media-gallery')) {
    customElements.define('media-gallery', RrProductGallery);
  }

  const accordionDuration = () => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return 0;
    }

    return 300;
  };

  const accordionTokens = new WeakMap();
  const accordionListeners = new WeakMap();

  const accordionPanel = (details) => details.querySelector('.rr-product__accordion-panel');
  const accordionClip = (details) => details.querySelector('.rr-product__accordion-clip');

  const clearAccordionInline = (panel) => {
    panel.style.height = '';
    panel.style.overflow = '';
    panel.style.transition = '';
  };

  const stopAccordionAnimation = (details) => {
    const previous = accordionListeners.get(details);

    if (previous) {
      previous.panel.removeEventListener('transitionend', previous.handler);
      accordionListeners.delete(details);
    }

    accordionTokens.set(details, (accordionTokens.get(details) || 0) + 1);
  };

  const animateAccordionHeight = (details, panel, from, to, onDone) => {
    const token = (accordionTokens.get(details) || 0) + 1;
    accordionTokens.set(details, token);

    const duration = accordionDuration();
    panel.style.overflow = 'hidden';
    panel.style.transition = 'none';
    panel.style.height = `${from}px`;
    void panel.offsetHeight;

    if (duration === 0 || from === to) {
      onDone(token);
      return;
    }

    const handler = (event) => {
      if (event.target !== panel || event.propertyName !== 'height') return;
      if (accordionTokens.get(details) !== token) return;

      panel.removeEventListener('transitionend', handler);
      accordionListeners.delete(details);
      onDone(token);
    };

    accordionListeners.set(details, { panel, handler });
    panel.addEventListener('transitionend', handler);
    panel.style.transition = `height ${duration}ms ease`;
    panel.style.height = `${to}px`;
  };

  const closeAccordion = (details) => {
    const panel = accordionPanel(details);
    const clip = accordionClip(details);

    if (!panel || !clip || !details.open) return;

    stopAccordionAnimation(details);
    details.classList.remove('is-open');

    const from = panel.getBoundingClientRect().height || clip.scrollHeight;
    animateAccordionHeight(details, panel, from, 0, (token) => {
      if (accordionTokens.get(details) !== token) return;

      details.open = false;
      clearAccordionInline(panel);
    });
  };

  const openAccordion = (details) => {
    const panel = accordionPanel(details);
    const clip = accordionClip(details);

    if (!panel || !clip) return;

    const group = details.closest('.rr-product__sidebar, .rr-product__below');

    if (group) {
      for (const other of group.querySelectorAll('.rr-product__accordion.is-open')) {
        if (other !== details) {
          closeAccordion(other);
        }
      }
    }

    stopAccordionAnimation(details);

    const wasOpen = details.open;
    details.open = true;
    details.classList.add('is-open');

    if (!wasOpen) {
      panel.style.overflow = 'hidden';
      panel.style.transition = 'none';
      panel.style.height = '0px';
    }

    const from = panel.getBoundingClientRect().height;
    const to = clip.scrollHeight;
    animateAccordionHeight(details, panel, from, to, (token) => {
      if (accordionTokens.get(details) !== token) return;

      clearAccordionInline(panel);
    });
  };

  const initAccordions = (root) => {
    const items = root.querySelectorAll('.rr-product__accordion');

    for (const details of items) {
      if (details.dataset.rrReady === 'true') continue;

      const summary = details.querySelector('.rr-product__accordion-summary');

      if (!summary) continue;

      details.dataset.rrReady = 'true';
      summary.addEventListener('click', (event) => {
        event.preventDefault();

        if (details.classList.contains('is-open')) {
          closeAccordion(details);
          return;
        }

        openAccordion(details);
      });
    }
  };

  document.addEventListener('shopify:section:load', (event) => {
    initAccordions(event.target);
  });

  initAccordions(document);
})();
